import React, { useEffect, useRef } from "react";
import { iso, addDays, startOfWeek, toMin, layoutDay } from "./calUtils";
import { EventChip, BlockChip } from "./EventChip";

const PX = 52;

export function TimeGrid({ view, cursor, byDate, closures, onOpen, onDay }) {
  const days = view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i)) : [cursor];
  const lists = days.map((d) => byDate[iso(d)] || []);
  const timed = lists.flat().filter((i) => !i.all_day && i.start_time);
  const minH = Math.max(0, Math.min(8, ...timed.map((i) => Math.floor(toMin(i.start_time) / 60))));
  const maxH = Math.min(24, Math.max(21, ...timed.map((i) => Math.ceil((toMin(i.end_time) ?? toMin(i.start_time) + 60) / 60))));
  const hours = Array.from({ length: maxH - minH }, (_, i) => minH + i);
  const today = iso(new Date());
  const scroller = useRef(null);
  useEffect(() => {
    const first = Math.min(...timed.map((i) => toMin(i.start_time)), 15 * 60);
    if (scroller.current) { scroller.current.scrollTop = Math.max(0, (first / 60 - minH - 0.5) * PX); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, iso(cursor)]);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const cols = `56px repeat(${days.length}, minmax(0, 1fr))`;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto" data-testid={`cal-${view}-view`}>
      <div className={view === "week" ? "min-w-[760px]" : ""}>
        <div className="grid border-b border-gray-100 bg-background" style={{ gridTemplateColumns: cols }}>
          <div />
          {days.map((d) => {
            const ds = iso(d);
            return (
              <button key={ds} type="button" data-testid={`cal-col-head-${ds}`} onClick={() => onDay(d)} className="py-2 text-center hover:bg-teal-light/40 transition-colors">
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
                <p className={`mx-auto mt-0.5 w-8 h-8 rounded-full flex items-center justify-center font-head font-bold ${ds === today ? "bg-teal text-white" : "text-gray-800"}`}>{d.getDate()}</p>
              </button>);
          })}
        </div>
        <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: cols }} data-testid="cal-allday-row">
          <div className="text-[10px] text-muted-foreground text-right pr-2 py-1.5">all-day</div>
          {lists.map((l, i) => (
            <div key={i} className={`border-l border-gray-100 p-1 space-y-0.5 min-h-[30px] ${closures.has(iso(days[i])) ? "bg-orange-50" : ""}`}>
              {l.filter((it) => it.all_day).map((it) => <EventChip key={it.uid} it={it} onOpen={onOpen} />)}
            </div>))}
        </div>
        <div ref={scroller} className="max-h-[62vh] overflow-y-auto">
          <div className="grid relative" style={{ gridTemplateColumns: cols }}>
            <div className="relative">{hours.map((h) => <div key={h} style={{ height: PX }} className="relative"><span className="absolute -top-1.5 right-2 text-[10px] text-muted-foreground">{h === minH ? "" : `${((h + 11) % 12) + 1} ${h >= 12 ? "PM" : "AM"}`}</span></div>)}</div>
            {lists.map((l, i) => (
              <div key={i} className="relative border-l border-gray-100" data-testid={`cal-col-${iso(days[i])}`}>
                {hours.map((h) => <div key={h} style={{ height: PX }} className="border-t border-gray-100" />)}
                {iso(days[i]) === today && nowMin >= minH * 60 && nowMin <= maxH * 60 &&
                  <div className="absolute left-0 right-0 h-0.5 bg-red-500 z-10" style={{ top: ((nowMin - minH * 60) / 60) * PX }}><span className="absolute -left-1 -top-1 w-2.5 h-2.5 rounded-full bg-red-500" /></div>}
                {layoutDay(l).map(({ item, s, e, lane, lanes }) => {
                  const h = ((e - s) / 60) * PX;
                  const w = Math.max(100 / lanes, lanes > 1 ? 60 : 100);
                  const left = lanes > 1 ? (lane * (100 - w)) / (lanes - 1) : 0;
                  return <BlockChip key={item.uid} it={item} onOpen={onOpen} small={h < 34}
                    style={{ top: ((s - minH * 60) / 60) * PX + 1, height: Math.max(h - 2, 20), left: `calc(${left}% + 2px)`, width: `calc(${w}% - 4px)`, zIndex: lane + 1 }} />;
                })}
              </div>))}
          </div>
        </div>
      </div>
    </div>);
}
